// The llama page as a wood-framed handheld: the game's picture frame
// (frame.png) bent round at the corners, and the CRT glass set inside it.
//
// The corners are not in the art, so they are made here, pixel by pixel. Each
// pixel's depth into the frame (its distance in from the rounded outer edge)
// picks the row of the frame's own wood band for that side, so the grain and
// its shading follow the curve. The corners mitre at 45 degrees, as a picture
// frame's do. Past the wood comes the CRT well's three-pixel bezel
// (crt-well.png), then the glass.

import { ART_SCALE, loadSprite, spriteCanvas } from './sprites';

// The outer corner radius, in sprite pixels.
const CORNER_RADIUS = 12;
// frame.png: 640x400, its wood bands run along each edge. The grain is read
// from between the corner pieces.
const FRAME_W = 640;
const FRAME_H = 400;
const FRAME_CORNER = 11;
// crt-well.png: 24x24, a 3px bezel round a dark middle.
const WELL_SIZE = 24;
const WELL_RIM = 3;
// Air between the bezel and what is on the glass.
const GLASS_PAD = 4;

type Side = 'top' | 'bottom' | 'left' | 'right';
type Rgba = [number, number, number, number];

interface Art {
  frame: ImageData;
  well: ImageData;
  // How deep the wood runs on each side, read off the art.
  band: Record<Side, number>;
}

export interface Device {
  root: HTMLElement;
  // The glass: everything the page shows goes in here.
  screen: HTMLElement;
}

const pixelOf = (image: ImageData, x: number, y: number): Rgba => {
  const at = (y * image.width + x) * 4;
  const d = image.data;
  return [d[at] ?? 0, d[at + 1] ?? 0, d[at + 2] ?? 0, d[at + 3] ?? 0];
};

function imageData(img: HTMLImageElement): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  context.drawImage(img, 0, 0);
  return context.getImageData(0, 0, img.width, img.height);
}

// The wood sample `depth` pixels in from the outer edge of `side`, at `along`
// pixels along that side.
function woodAt(frame: ImageData, side: Side, depth: number, along: number): Rgba {
  const spanX = FRAME_W - FRAME_CORNER * 2;
  const spanY = FRAME_H - FRAME_CORNER * 2;
  const x = FRAME_CORNER + (((along % spanX) + spanX) % spanX);
  const y = FRAME_CORNER + (((along % spanY) + spanY) % spanY);
  if (side === 'top') return pixelOf(frame, x, depth);
  if (side === 'bottom') return pixelOf(frame, x, FRAME_H - 1 - depth);
  if (side === 'left') return pixelOf(frame, depth, y);
  return pixelOf(frame, FRAME_W - 1 - depth, y);
}

// The bezel row `depth` in from the glass edge of `side`; the dark middle past it.
function wellAt(well: ImageData, side: Side, depth: number): Rgba {
  const middle = WELL_SIZE / 2;
  if (depth >= WELL_RIM) return pixelOf(well, middle, middle);
  if (side === 'top') return pixelOf(well, middle, depth);
  if (side === 'bottom') return pixelOf(well, middle, WELL_SIZE - 1 - depth);
  if (side === 'left') return pixelOf(well, depth, middle);
  return pixelOf(well, WELL_SIZE - 1 - depth, middle);
}

function bandDepth(frame: ImageData, side: Side): number {
  let depth = 0;
  while (depth < FRAME_CORNER && woodAt(frame, side, depth, 0)[3] > 0) depth += 1;
  return depth;
}

// Which side a pixel belongs to and how deep it sits, for a box w by h with
// rounded corners.
function placeOf(x: number, y: number, w: number, h: number): { side: Side; depth: number } {
  const cx = x + 0.5;
  const cy = y + 0.5;
  const px = Math.abs(cx - w / 2) - (w / 2 - CORNER_RADIUS);
  const py = Math.abs(cy - h / 2) - (h / 2 - CORNER_RADIUS);
  const vertical: Side = cy < h / 2 ? 'top' : 'bottom';
  const horizontal: Side = cx < w / 2 ? 'left' : 'right';
  if (px > 0 && py > 0) {
    const depth = Math.floor(CORNER_RADIUS - Math.hypot(px, py));
    return { side: py > px ? vertical : horizontal, depth };
  }
  const dx = Math.min(cx, w - cx);
  const dy = Math.min(cy, h - cy);
  return dx < dy
    ? { side: horizontal, depth: Math.floor(dx) }
    : { side: vertical, depth: Math.floor(dy) };
}

// Paints the whole device, w by h sprite pixels, into a sprite-sized image.
function paintDevice(art: Art, w: number, h: number): ImageData {
  const out = new ImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const { side, depth } = placeOf(x, y, w, h);
      if (depth < 0) continue;
      const along = side === 'top' || side === 'bottom' ? x : y;
      const wood = depth < art.band[side] ? woodAt(art.frame, side, depth, along) : null;
      const color = wood && wood[3] > 0 ? wood : wellAt(art.well, side, depth - art.band[side]);
      out.data.set(color, (y * w + x) * 4);
    }
  }
  return out;
}

let artLoaded: Promise<Art> | null = null;
let shownScreen: HTMLElement | null = null;
// Fallback width before the device is on the page: a phone's glass.
const GLASS_FALLBACK_PX = 320;

// The width text may use on the glass, in CSS pixels.
export function glassWidth(): number {
  if (!shownScreen) return GLASS_FALLBACK_PX;
  const style = getComputedStyle(shownScreen);
  const pad = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
  return Math.max(0, shownScreen.clientWidth - pad) || GLASS_FALLBACK_PX;
}

function loadArt(): Promise<Art> {
  artLoaded ??= Promise.all([loadSprite('/ui/frame.png'), loadSprite('/ui/crt-well.png')]).then(
    ([frameImg, wellImg]) => {
      const frame = imageData(frameImg);
      const band = {
        top: bandDepth(frame, 'top'),
        bottom: bandDepth(frame, 'bottom'),
        left: bandDepth(frame, 'left'),
        right: bandDepth(frame, 'right'),
      };
      return { frame, well: imageData(wellImg), band };
    },
  );
  return artLoaded;
}

// The device and its glass. The wood redraws whenever the device changes size,
// and the glass is inset by each side's own wood, bezel and pad.
export function makeDevice(): Device {
  const root = document.createElement('div');
  root.className = 'device';
  const wood = document.createElement('canvas');
  wood.className = 'device-wood';
  wood.setAttribute('aria-hidden', 'true');
  const screen = document.createElement('div');
  screen.className = 'device-screen';
  root.append(wood, screen);
  shownScreen = screen;
  const sprite = document.createElement('canvas');
  const redraw = (): void => {
    const w = Math.floor(root.clientWidth / ART_SCALE);
    const h = Math.floor(root.clientHeight / ART_SCALE);
    if (w <= CORNER_RADIUS * 2 || h <= CORNER_RADIUS * 2) return;
    void loadArt().then((art) => {
      for (const side of ['top', 'bottom', 'left', 'right'] as Side[]) {
        const inset = (art.band[side] + WELL_RIM + GLASS_PAD) * ART_SCALE;
        root.style.setProperty(`--glass-${side}`, `${inset}px`);
      }
      sprite.width = w;
      sprite.height = h;
      (sprite.getContext('2d') as CanvasRenderingContext2D).putImageData(
        paintDevice(art, w, h),
        0,
        0,
      );
      spriteCanvas(wood, w, h, ART_SCALE).drawImage(sprite, 0, 0);
    });
  };
  new ResizeObserver(redraw).observe(root);
  return { root, screen };
}
